import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';

import { URLService } from './dynamic-url.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

/**
 * Writes the switcher sidebar's folders.
 *
 * Every mutation is fire-and-forget: the sidebar applies it locally first and never waits on the
 * round trip, the same contract as pinning. Errors are swallowed (the HTTP interceptor still
 * surfaces them) — the UI has already moved on, so there is nothing to roll back into, and the next
 * load re-reads the real list from the API.
 *
 * Writes are queued and issued one at a time. Unlike pin/unpin, folder operations are
 * order-dependent: the POST that creates a folder has to land before the spaces list for a space
 * dropped into it, or that write 404s.
 */
@Injectable({ providedIn: 'root' })
export class SpaceFoldersService {
  private readonly writes$ = new Subject<Observable<unknown>>();

  constructor(private http: HttpClient, private urlService: URLService) {
    this.writes$
      .pipe(
        concatMap((request$) =>
          // Caught per request, so one failure doesn't tear down the queue for every later write.
          request$.pipe(catchError(() => of(null))),
        ),
      )
      .subscribe();
  }

  private get foldersUrl(): string {
    return `${this.urlService.getDynamicUrl()}/spaces/folders`;
  }

  /** Queues a write. Returns immediately — callers have already updated the UI. */
  private enqueue(request$: Observable<unknown>): void {
    this.writes$.next(request$);
  }

  /**
   * Creates a folder. `_id` is generated by the caller so the section can be rendered, renamed and
   * dropped into before this request resolves.
   */
  createFolder(id: string, title: string): void {
    this.enqueue(this.http.post(this.foldersUrl, JSON.stringify({ _id: id, title }), httpOptions));
  }

  renameFolder(id: string, title: string): void {
    this.enqueue(
      this.http.patch(`${this.foldersUrl}/${id}`, JSON.stringify({ title }), httpOptions),
    );
  }

  deleteFolder(id: string): void {
    this.enqueue(this.http.delete(`${this.foldersUrl}/${id}`));
  }

  /**
   * Replaces a folder's spaces with `spaceIds`, in that order. Filing, removing and reordering are
   * all this one call — send the list you want to end up with, exactly as for pinned spaces.
   */
  setFolderSpaces(id: string, spaceIds: string[]): void {
    this.enqueue(
      this.http.put(`${this.foldersUrl}/${id}/spaces`, JSON.stringify({ spaceIds }), httpOptions),
    );
  }

  /**
   * Moves a folder between two neighbours. Sends the neighbours rather than an index so the server
   * owns sort-key generation and the move stays a single-document write. `null` at either end means
   * "no neighbour" — the top or bottom of the list.
   */
  moveFolder(id: string, previousFolderId: string | null, nextFolderId: string | null): void {
    this.enqueue(
      this.http.put(
        `${this.foldersUrl}/${id}/position`,
        JSON.stringify({ previousFolderId, nextFolderId }),
        httpOptions,
      ),
    );
  }
}
